import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, Home, ShoppingBag, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CheckoutSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/stripe/status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check status');
        }
        
        const data = await response.json();
        setPaymentInfo(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          return true;
        } else if (data.status === 'expired' || data.payment_status === 'failed') {
          setStatus('failed');
          return true;
        }
        
        return false;
      } catch (err) {
        console.error('Error polling status:', err);
        return false;
      }
    };

    const poll = async () => {
      const done = await pollStatus();
      
      if (!done && pollAttempts < maxAttempts) {
        setPollAttempts(prev => prev + 1);
        setTimeout(poll, 2000); // Poll toutes les 2 secondes
      } else if (!done) {
        setStatus('error');
      }
    };

    poll();
  }, [sessionId, pollAttempts]);

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark">
      <Header />
      
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800">
          <CardHeader className="text-center">
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
                <CardTitle className="text-white">Vérification du paiement...</CardTitle>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <CardTitle className="text-white">Paiement réussi !</CardTitle>
              </>
            )}
            
            {status === 'failed' && (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <CardTitle className="text-white">Paiement échoué</CardTitle>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-400" />
                </div>
                <CardTitle className="text-white">Erreur de vérification</CardTitle>
              </>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6">
            {status === 'loading' && (
              <p className="text-center text-zinc-400">
                Veuillez patienter pendant que nous vérifions votre paiement...
              </p>
            )}
            
            {status === 'success' && paymentInfo && (
              <>
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Montant</span>
                    <span className="text-white font-semibold">
                      {paymentInfo.amount_total} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  {paymentInfo.order_id && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Commande</span>
                      <span className="text-white font-mono">{paymentInfo.order_id}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-center text-zinc-400">
                  Merci pour votre commande ! Vous recevrez un email de confirmation.
                </p>
                
                <div className="flex gap-3">
                  <Button 
                    onClick={() => navigate('/')} 
                    variant="outline" 
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Accueil
                  </Button>
                  <Button 
                    onClick={() => navigate('/products')} 
                    className="flex-1 bg-mutagim-red hover:bg-red-700"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Continuer
                  </Button>
                </div>
              </>
            )}
            
            {status === 'failed' && (
              <>
                <p className="text-center text-zinc-400">
                  Votre paiement n'a pas pu être traité. Veuillez réessayer ou choisir un autre moyen de paiement.
                </p>
                
                <Button 
                  onClick={() => navigate('/checkout')} 
                  className="w-full bg-mutagim-red hover:bg-red-700"
                >
                  Réessayer le paiement
                </Button>
              </>
            )}
            
            {status === 'error' && (
              <>
                <p className="text-center text-zinc-400">
                  Nous n'avons pas pu vérifier le statut de votre paiement. Veuillez vérifier votre email ou contacter le support.
                </p>
                
                <Button 
                  onClick={() => navigate('/')} 
                  variant="outline" 
                  className="w-full"
                >
                  Retour à l'accueil
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
}

export default CheckoutSuccessPage;
